import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { 
  Lock, 
  Bell, 
  Shield, 
  LogOut,
  AlertTriangle,
  Settings,
  ChevronRight
} from 'lucide-react';
import ChangePasswordForm from './components/ChangePasswordForm';
import NotificationSettings from './components/NotificationSettings';
import TwoFactorAuth from './components/TwoFactorAuth';
import SessionManagement from './components/SessionManagement';
import WarningsTab from './components/WarningsTab';
import useSettingsStore from '../../store/settingsStore';
import useAuthStore from '../../store/authStore';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { loadSettings, isLoading } = useSettingsStore();
  const { user } = useAuthStore();

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const tabs = [
    { name: 'Security', icon: Lock, component: ChangePasswordForm, description: 'Change your password' },
    { name: 'Two-Factor Auth', icon: Shield, component: TwoFactorAuth, description: 'Add extra security layer' },
    { name: 'Sessions', icon: LogOut, component: SessionManagement, description: 'Manage active devices' },
    { name: 'Warnings', icon: AlertTriangle, component: WarningsTab, description: 'View account warnings' },
  ];

  if (isLoading && !window.location.pathname.includes('settings')) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6 p-3 sm:p-0">
      {/* Header - Mobile Optimized */}
      <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-5 shadow-neomorph border border-primary-100/50">
        <div className="flex items-center">
          <div className="w-1 h-5 sm:h-8 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2 sm:mr-3" />
          <div>
            <h1 className="text-base sm:text-xl font-bold text-gray-800">Settings</h1>
            <p className="text-[11px] sm:text-sm text-gray-500 mt-0.5 hidden sm:block">
              Manage your account settings and preferences
            </p>
            <p className="text-[10px] text-gray-500 mt-0 sm:hidden">
              Account preferences
            </p>
          </div>
        </div>
      </div>

      {/* Settings Tabs Card */}
      <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
        {/* Mobile: Tab Picker Dropdown */}
        <div className="sm:hidden p-3 bg-[#E8ECF5] border-b border-primary-100/50">
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="w-full flex items-center justify-between p-3 bg-white rounded-lg shadow-neomorph-inset"
          >
            <div className="flex items-center">
              <div className={`p-1.5 rounded-md mr-2.5 ${tabs[activeTab].icon === Lock ? 'bg-primary-100' : 'bg-gray-100'}`}>
                {React.createElement(tabs[activeTab].icon, { className: `h-4 w-4 ${tabs[activeTab].icon === Lock ? 'text-primary-600' : 'text-gray-500'}` })}
              </div>
              <div className="text-left">
                <p className="text-xs font-medium text-gray-800">{tabs[activeTab].name}</p>
                <p className="text-[10px] text-gray-500">{tabs[activeTab].description}</p>
              </div>
            </div>
            <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${showMobileMenu ? 'rotate-90' : ''}`} />
          </button>
          
          {showMobileMenu && (
            <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
              {tabs.map((tab, index) => (
                <button
                  key={tab.name}
                  onClick={() => {
                    setActiveTab(index);
                    setShowMobileMenu(false);
                  }}
                  className={`w-full flex items-center p-3 rounded-lg transition-all ${
                    activeTab === index
                      ? 'bg-primary-50 shadow-neomorph-inset'
                      : 'bg-white shadow-neomorph hover:shadow-neomorph-hover'
                  }`}
                >
                  <div className={`p-1.5 rounded-md mr-2.5 ${
                    activeTab === index ? 'bg-primary-100' : 'bg-gray-100'
                  }`}>
                    <tab.icon className={`h-4 w-4 ${
                      activeTab === index ? 'text-primary-600' : 'text-gray-500'
                    }`} />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-xs font-medium text-gray-800">{tab.name}</p>
                    <p className="text-[10px] text-gray-500">{tab.description}</p>
                  </div>
                  {activeTab === index && (
                    <div className="w-1 h-8 bg-primary-500 rounded-full ml-2" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Desktop: Tab List */}
        <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
          <div className="hidden sm:block border-b border-primary-100/50 bg-[#E8ECF5] px-2 overflow-x-auto">
            <Tab.List className="flex flex-nowrap sm:flex-wrap -mb-px min-w-max sm:min-w-0">
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.name}
                  className={({ selected }) =>
                    classNames(
                      'py-3 sm:py-4 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap transition-all',
                      selected
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )
                  }
                >
                  {({ selected }) => (
                    <>
                      <div className={`p-1 rounded-md sm:p-1.5 mr-1.5 sm:mr-2 transition-all ${
                        selected ? 'bg-primary-100' : 'bg-gray-100'
                      }`}>
                        <tab.icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${
                          selected ? 'text-primary-600' : 'text-gray-500'
                        }`} />
                      </div>
                      {tab.name}
                    </>
                  )}
                </Tab>
              ))}
            </Tab.List>
          </div>
          
          <Tab.Panels className="p-4 sm:p-6">
            {tabs.map((tab, index) => (
              <Tab.Panel key={index}>
                <tab.component />
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

export default SettingsPage;